import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Flower2, Lock, BookOpen, Sparkles, Award, CloudOff } from 'lucide-react';
import { Card } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Progress } from '../components/ui/progress';
import BottomNav from '../components/BottomNav';
import { modulesAPI } from '../services/api';
import { toast } from '../hooks/use-toast';
import { useAuth } from '../context/AuthContext';
import { useOffline } from '../context/OfflineContext';

const Modules = () => {
  const navigate = useNavigate();
  const { isAuthenticated, loading: authLoading } = useAuth();
  const { isOnline } = useOffline();
  const [modules, setModules] = useState([]);
  const [loading, setLoading] = useState(true);
  const [isStaleData, setIsStaleData] = useState(false);

  useEffect(() => {
    if (authLoading) return;
    if (!isAuthenticated) {
      navigate('/login');
      return;
    }

    const fetchModules = async () => {
      try {
        const response = await modulesAPI.getAll('education');
        const data = response.data || response;
        setModules(data.modules || []);
        if (response._offline) setIsStaleData(true);
      } catch (error) {
        console.error('Error fetching modules:', error);
        toast({
          title: 'Erreur',
          description: 'Impossible de charger les modules',
          variant: 'destructive'
        });
      }
      setLoading(false);
    };

    fetchModules();
  }, [isAuthenticated, navigate, isOnline, authLoading]);
  // eslint-disable-next-line -- fetchModules is defined inside the effect

  const getModuleIcon = (icon) => {
    return <span className="text-4xl">{icon}</span>;
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-purple-900 via-pink-800 to-purple-900 flex items-center justify-center">
        <div className="text-white text-xl">Chargement...</div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-purple-900 via-pink-800 to-purple-900 pb-24">
      {/* Offline/Stale Data Banner */}
      {(!isOnline || isStaleData) && (
        <div className="bg-orange-500/20 border-b border-orange-400/30 px-4 py-2 flex items-center gap-2 text-orange-200 text-sm">
          <CloudOff className="w-4 h-4" />
          {!isOnline ? 'Mode hors ligne - Données en cache' : 'Données mises en cache - Actualisation en cours...'}
        </div>
      )}
      
      {/* Header */}
      <div className="sticky top-0 bg-purple-900/80 backdrop-blur-md border-b border-white/10 px-4 py-4 z-40">
        <div className="flex items-center gap-3">
          <Flower2 className="w-8 h-8 text-pink-300" />
          <h1 className="text-2xl font-bold text-white">Modules</h1>
        </div>
      </div>

      <div className="max-w-md mx-auto px-4 py-6 space-y-4">
        {/* Unlock Info Card */}
        <Card className="p-6 bg-gradient-to-br from-yellow-500/20 to-orange-500/20 backdrop-blur-md border-yellow-400/30">
          <div className="flex items-start gap-3">
            <Lock className="w-6 h-6 text-yellow-400 mt-1" />
            <div>
              <h3 className="text-white font-semibold mb-2">Débloquez plus de contenu</h3>
              <p className="text-pink-100 text-sm">
                Complétez les modules "Cycle Menstruel" et "Contraception"
              </p>
            </div>
          </div>
        </Card>

        {/* Modules List */}
        {modules.map((module) => (
          <Card 
            key={module.id}
            className={`p-6 backdrop-blur-md border-white/20 transition-all ${
              module.locked 
                ? 'bg-white/5 opacity-75' 
                : 'bg-white/10 hover:bg-white/15 cursor-pointer'
            }`}
            onClick={() => !module.locked && navigate(`/module/${module.id}`)}
          >
            <div className="flex items-start gap-4">
              <div 
                className="w-16 h-16 rounded-2xl flex items-center justify-center relative"
                style={{ backgroundColor: `${module.color}40` }}
              >
                {getModuleIcon(module.icon)}
                {module.locked && (
                  <div className="absolute inset-0 bg-black/50 rounded-2xl flex items-center justify-center">
                    <Lock className="w-6 h-6 text-white" />
                  </div>
                )}
              </div>

              <div className="flex-1">
                <h3 className="text-white font-semibold text-lg mb-2">{module.title}</h3>
                <p className="text-pink-200 text-sm mb-3">{module.description}</p>
                
                <div className="flex items-center gap-4 text-xs text-pink-300">
                  <span>{module.lessons} leçons</span>
                  <span>•</span>
                  <span>{module.duration}</span>
                  <span>•</span>
                  <span>{module.level}</span>
                </div>

                {!module.locked && module.progress > 0 && (
                  <div className="mt-3">
                    <Progress value={module.progress} className="h-2 bg-purple-900/50" />
                  </div>
                )}
              </div>
            </div>
          </Card>
        ))}
      </div>

      <BottomNav active="modules" />
    </div>
  );
};

export default Modules;
